import type {
  PerformanceDataPoint,
  PerformanceHistoryRow,
} from "@/components/fund/leveraged-perf-chart";
import type { PremiumDiscountQuarterlyRow } from "@/components/fund/leveraged-premium-discount";
import { fetchDailyNav } from "@/data/etf-api";
import type { ApiDailyNavRecord } from "@/data/etf-api";
import type { FundDetail } from "@/data/fund-detail-types";
import {
  buildPdChart,
  buildQuarterlyTable,
  computePerformanceHistory,
  toNum,
} from "@/lib/chart-transforms";
/**
 * React-Query hook: fetch live daily NAV data and transform it for the
 * performance chart and premium/discount chart components.
 *
 * Ported from dane `frontend/hooks/use-etf-live-nav.ts`, adapted to use
 * React Query instead of useState/useEffect, and consuming etfsite's
 * `fetchDailyNav` and chart-transforms helpers.
 */
import { useQuery } from "@tanstack/react-query";

/** A single P/D data point for the hover tooltip. */
export interface PdDataPoint {
  date: string;
  pd: number;
  nav: number;
  mkt: number;
}

export interface LiveNavData {
  /** Performance chart: daily NAV + market price points. */
  performanceData: PerformanceDataPoint[];
  /** Performance history table: return calculations. */
  performanceHistory: PerformanceHistoryRow[];
  /** Premium/discount chart: SVG polyline points. */
  pdChartPoints: string;
  /** Premium/discount chart: X-axis date labels. */
  pdDateLabels: string[];
  /** Premium/discount chart: per-point data for hover tooltips. */
  pdDataPoints: PdDataPoint[];
  /** Premium/discount table: quarterly aggregations. */
  pdQuarterlyData: PremiumDiscountQuarterlyRow[];
  /** Latest NAV value. */
  latestNav: string;
  /** Latest market price. */
  latestMarketPrice: string;
  /** Latest premium/discount. */
  latestPremiumDiscount: string;
}

function transformNavData(records: ApiDailyNavRecord[]): LiveNavData {
  // Build performance data points
  const performanceData: PerformanceDataPoint[] = records.map((r) => ({
    date: r.rate_date.split("T")[0] ?? r.rate_date,
    nav: toNum(r.nav),
    mkt: toNum(r.market_price),
  }));

  // Compute performance returns
  const performanceHistory = computePerformanceHistory(records);

  // Build P/D chart
  const { chartPoints, dateLabels, dataPoints: pdDataPoints } = buildPdChart(records);

  // Build P/D quarterly table
  const pdQuarterlyData = buildQuarterlyTable(records);

  // Latest values
  const latest = records[records.length - 1]!;

  return {
    performanceData,
    performanceHistory,
    pdChartPoints: chartPoints,
    pdDateLabels: dateLabels,
    pdDataPoints,
    pdQuarterlyData,
    latestNav: `$${toNum(latest.nav).toFixed(2)}`,
    latestMarketPrice: `$${toNum(latest.market_price).toFixed(2)}`,
    latestPremiumDiscount: `${toNum(latest.premium_discount_percentage) >= 0 ? "+" : ""}${toNum(latest.premium_discount_percentage).toFixed(2)}%`,
  };
}

export function useLiveNav(fund: FundDetail): { data: LiveNavData | null; isLoading: boolean } {
  const ticker = fund.ticker;

  const { data: result, isPending } = useQuery({
    queryKey: ["daily-nav", ticker],
    queryFn: () => fetchDailyNav(ticker),
    enabled: Boolean(ticker),
    staleTime: 60_000,
    retry: false,
  });

  if (isPending) return { data: null, isLoading: true };
  if (!result || result.records.length === 0) return { data: null, isLoading: false };
  return { data: transformNavData(result.records), isLoading: false };
}
